import {getQepSystem} from '@qep/shared/src';
import {
  AlgoStatusString, type SingleAlgoFullReport, type OrderRecord, AlgoStatusStringHumanReadable, OrderState, type QEPNotificationPayload, QEPNotificationType, QEPNotificationStatus,
} from '@qep/types/src';

export const processOrderNotification = (order: OrderRecord, user: any): QEPNotificationPayload<OrderRecord> | undefined => {
  if (!order || order.traderId !== user.traderId) {
    return;
  }

  return {
    id: order.orderId,
    type: QEPNotificationType.ORDER,
    title: `${getOrderTitle(order)}`,
    shortDescription: `${order.orderId} updated`,
    payload: order,
    status: QEPNotificationStatus.LOW,
    timestamp: new Date().toISOString(),
  };
};

export const processAlgoNotification = (algo: SingleAlgoFullReport, user: any): QEPNotificationPayload<SingleAlgoFullReport> | undefined => {
  if (!algo || algo.accountInfo?.traderId !== user.traderId) {
    return;
  }

  return {
    id: algo.algoRunId,
    type: QEPNotificationType.ALGO,
    title: `${getAlgoTitle(algo)}`,
    shortDescription: `${algo.algoRunId} updated`,
    payload: algo,
    status: QEPNotificationStatus.LOW,
    timestamp: new Date().toISOString(),
  };
};

export const getAlgoTitle = (algo: SingleAlgoFullReport): string => `Algo ${algo.algoRunId}-${(getHummanReadableAlgoStatus(algo))}`;

export const getOrderTitleBase = (order: OrderRecord): string => `Order ${order.orderId}-`;
export const getOrderTitle = (order: OrderRecord): string => {
  if (getHummanReadableOrderStatus(order) === '') {
    return '';
  }

  return `${getOrderTitleBase(order)}${(getHummanReadableOrderStatus(order))}`;
};

const getHummanReadableAlgoStatus = (algo: SingleAlgoFullReport): string => {
  const {algoStatus} = algo;
  // eslint-disable-next-line @typescript-eslint/switch-exhaustiveness-check
  switch (algoStatus as unknown as AlgoStatusString) {
    case AlgoStatusString.ALGOSTATUS_ABORTED_BY_SYSTEM: {
      return AlgoStatusStringHumanReadable.ALGOSTATUS_ABORTED_BY_SYSTEM;
    }

    case AlgoStatusString.ALGOSTATUS_ABORTED: {
      return AlgoStatusStringHumanReadable.ALGOSTATUS_ABORTED;
    }

    case AlgoStatusString.ALGOSTATUS_ERROR: {
      return AlgoStatusStringHumanReadable.ALGOSTATUS_ERROR;
    }

    case AlgoStatusString.ALGOSTATUS_PAUSED: {
      return AlgoStatusStringHumanReadable.ALGOSTATUS_PAUSED;
    }

    case AlgoStatusString.ALGOSTATUS_INCOMPLETE_REACHED_END_TIME: {
      return AlgoStatusStringHumanReadable.ALGOSTATUS_INCOMPLETE_REACHED_END_TIME;
    }

    case AlgoStatusString.ALGOSTATUS_INCOMPLETE_MARKET_CLOSED: {
      return AlgoStatusStringHumanReadable.ALGOSTATUS_INCOMPLETE_MARKET_CLOSED;
    }

    case AlgoStatusString.ALGOSTATUS_RUNNING: {
      return AlgoStatusStringHumanReadable.ALGOSTATUS_RUNNING;
    }

    case AlgoStatusString.ALGOSTATUS_COMPLETE: {
      return AlgoStatusStringHumanReadable.ALGOSTATUS_COMPLETE;
    }

    case AlgoStatusString.ALGOSTATUS_INITIALIZED: {
      return AlgoStatusStringHumanReadable.ALGOSTATUS_INITIALIZED;
    }

    case AlgoStatusString.ALGOSTATUS_RESUME_REQUESTED: {
      return AlgoStatusStringHumanReadable.ALGOSTATUS_RESUME_REQUESTED;
    }

    case AlgoStatusString.ALGOSTATUS_PAUSE_REQUESTED: {
      return AlgoStatusStringHumanReadable.ALGOSTATUS_PAUSE_REQUESTED;
    }

    case AlgoStatusString.ALGOSTATUS_LAUNCH_REQUESTED: {
      return AlgoStatusStringHumanReadable.ALGOSTATUS_LAUNCH_REQUESTED;
    }

    case AlgoStatusString.ALGOSTATUS_ABORT_REQUESTED: {
      return AlgoStatusStringHumanReadable.ALGOSTATUS_ABORT_REQUESTED;
    }

    default: {
      return algoStatus as unknown as AlgoStatusStringHumanReadable;
    }
  }
};

const getHummanReadableOrderStatus = (order: OrderRecord): string => {
  let value: number | string = Number(order?.status);
  if (Number.isNaN(value)) {
    value = order?.status ?? '';
  }

  switch (value) {
    case OrderState.ORDERSTATE_ACKNOWLEDGED: {
      return 'ACKNOWLEDGE';
    }

    case OrderState.ORDERSTATE_FILLED: {
      return 'FILLED';
    }

    case OrderState.ORDERSTATE_PARTIAL_FILLED: {
      return 'PARTIAL';
    }

    case OrderState.ORDERSTATE_PENDING_NEW: {
      return 'PENDING NEW';
    }

    case OrderState.ORDERSTATE_NEW: {
      return 'OPEN';
    }

    case OrderState.ORDERSTATE_CANCELED: {
      return 'CANCELED';
    }

    case OrderState.ORDERSTATE_PENDING_CANCEL: {
      return 'PENDING CANCEL';
    }

    case OrderState.ORDERSTATE_REJECTED: {
      return 'REJECTED';
    }

    case OrderState.ORDERSTATE_PENDING_REPLACE: {
      return 'PENDING REPLACE';
    }

    case OrderState.ORDERSTATE_EXPIRED: {
      return 'EXPIRED';
    }

    case OrderState.ORDERSTATE_DONE_FOR_DAY: {
      return 'DONE FOR THE DAY';
    }

    case OrderState.ORDERSTATE_STOPPED: {
      return 'STOPPED';
    }

    case OrderState.ORDERSTATE_SUSPENDED: {
      return 'SUSPENDED';
    }

    case OrderState.ORDERSTATE_CALCULATED: {
      return 'CALCULCATED';
    }

    case OrderState.ORDERSTATE_ACCEPTED_FOR_BIDDING: {
      return 'ACCEPTED';
    }

    default: {
      return '';
    }
  }
};
